import { Suspense } from 'react';
import { useMediaQuery } from 'react-responsive';
import { Outlet, useLocation } from 'react-router';

import useAccountQuery from 'api/queries/useAccountQuery';
import CartSheet from 'features/shop/cart/CartSheet';
import { ProductCardProvider } from 'features/shop/ProductCardV2/ProductCardProvider';
import {
    AddToRecurringModal,
    dismissRecurringLimitWarning,
    RecurringLimitWarningModal,
    setSuppressRecurringModal,
    setSuppressSoldOutRecurringModal,
    SoldOutRecurringModal,
} from 'features/shop/recurringModals';
import { BackInStockWaitlistModal } from 'features/shop/waitlist';
import BackInStockWaitlistSuccessModal from 'features/shop/waitlist/BackInStockWaitlistSuccessModal';
import useBackInStockWaitlistSuccessModal from 'features/shop/waitlist/useBackInStockWaitlistSuccessModal';
import Loader from 'honeydew/Loader';
import useGlobalStore from 'hooks/useGlobalStore';
import useTailwindTheme from 'hooks/useTailwindTheme';
import GwpErrorToast from 'shared/GiftWithPurchase/GwpErrorToast';
import { trackEvent } from 'utils/analyticsUtils';

function ProductDetailPageLayout() {
    const { data: account } = useAccountQuery();
    const {
        backInStockWaitlistSuccessModalOpen,
        onCloseBackInStockWaitlistSuccessModal,
        products: backInStockWaitlistProducts,
    } = useBackInStockWaitlistSuccessModal();
    const {
        autocompleteOpen,
        backInStockWaitlistModal,
        recurringLimitWarningModal,
        recurringModal,
        searchQuery,
        setBackInStockWaitlistModal,
        setRecurringLimitWarningModal,
        setRecurringModal,
        setSoldOutRecurringModal,
        soldOutRecurringModal,
    } = useGlobalStore();
    const location = useLocation();

    const tailwindTheme = useTailwindTheme();
    const mobile = useMediaQuery({ maxWidth: tailwindTheme?.screens?.md });
    const searchAutocompleteOpen = autocompleteOpen && searchQuery !== '';

    const handleCloseRecurringModal = (suppressFutureModals) => {
        if (suppressFutureModals) {
            setSuppressRecurringModal(true);
        }
        setRecurringModal({ open: false, itemName: '' });
        trackEvent('recurring modal closed', {
            path: window.location.pathname,
        });
    };

    const handleCloseSoldOutRecurringModal = () => {
        setSoldOutRecurringModal({ open: false, itemName: '', externalSku: '', onConfirm: null });
    };

    const handleAddToRecurring = (suppressFutureModals) => {
        if (suppressFutureModals) {
            setSuppressSoldOutRecurringModal(true);
        }
        soldOutRecurringModal.onConfirm?.();
        setSoldOutRecurringModal({ open: false, itemName: '', externalSku: '', onConfirm: null });
    };

    const handleCloseRecurringLimitWarning = () => {
        dismissRecurringLimitWarning();
        setRecurringLimitWarningModal(false);
    };

    const handleCloseBackInStockWaitlistModal = () => {
        setBackInStockWaitlistModal({ open: false, productData: null, source: null });
    };

    if (!account?.nextOrder?.chargeID) {
        return (
            <div className="mt-[140px] flex justify-center">
                <Loader />
            </div>
        );
    }

    const productData = location?.state?.productData ?? null;

    return (
        <Suspense fallback={<Loader />}>
            {!mobile && !searchAutocompleteOpen && <CartSheet orderMinOnly />}
            <Outlet context={{ productData }} />
            <AddToRecurringModal
                isOpen={recurringModal.open}
                itemName={recurringModal.itemName}
                onClose={handleCloseRecurringModal}
            />
            <SoldOutRecurringModal
                isOpen={soldOutRecurringModal.open}
                itemName={soldOutRecurringModal.itemName}
                onAddToRecurring={handleAddToRecurring}
                onClose={handleCloseSoldOutRecurringModal}
            />
            <RecurringLimitWarningModal
                isOpen={recurringLimitWarningModal}
                onClose={handleCloseRecurringLimitWarning}
            />
            <BackInStockWaitlistModal
                isOpen={backInStockWaitlistModal.open}
                productData={backInStockWaitlistModal.productData}
                onClose={handleCloseBackInStockWaitlistModal}
            />
            <ProductCardProvider>
                <BackInStockWaitlistSuccessModal
                    open={backInStockWaitlistSuccessModalOpen}
                    onClose={onCloseBackInStockWaitlistSuccessModal}
                    products={backInStockWaitlistProducts}
                />
            </ProductCardProvider>
            <GwpErrorToast />
        </Suspense>
    );
}

export default ProductDetailPageLayout;
